import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Loader2, Building2, ShieldAlert, ArrowRight, Check,
} from "lucide-react";
import { ROLES } from "./constants";

// Modal for editing an existing user: change name / job title, switch role,
// or move them to a different organization. Promoting a user to "admin"
// removes their org membership; demoting back to a non-admin requires
// re-picking an org. Backend ends the user's existing sessions whenever
// role or organization changes, so we surface that fact in a toast.
export function EditUserModal({ open, onOpenChange, user, organizations, onSaved }) {
  const [form, setForm] = useState({ name: "", company: "", role: "client_user", organization_id: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && user) {
      setForm({
        name: user.name || "",
        company: user.company || "",
        role: user.role || "client_user",
        organization_id: user.organization_id || "",
      });
    }
  }, [open, user]);

  if (!user) return null;
  const currentOrgName = organizations.find((o) => o.id === user.organization_id)?.name || (user.organization_id ? "Unknown org" : "—");
  const newOrgName = form.role === "admin"
    ? "(no organization — Platform Admin)"
    : (organizations.find((o) => o.id === form.organization_id)?.name || "");
  const orgChanged = form.role !== "admin" && form.organization_id && form.organization_id !== user.organization_id;
  const roleBecomingAdmin = form.role === "admin" && user.role !== "admin";

  const onSubmit = async (e) => {
    e.preventDefault();
    const updates = {};
    if (form.name.trim() !== (user.name || "")) updates.name = form.name.trim();
    if (form.company.trim() !== (user.company || "")) updates.company = form.company.trim();
    if (form.role !== user.role) updates.role = form.role;
    if (form.role === "admin") {
      if (user.organization_id) updates.organization_id = "";
    } else {
      if (!form.organization_id) { toast.error("Pick an organization for this user."); return; }
      if (form.organization_id !== user.organization_id) updates.organization_id = form.organization_id;
    }
    if (Object.keys(updates).length === 0) { toast.info("No changes to save."); return; }

    setBusy(true);
    try {
      await api.patch(`/admin/users/${user.id}`, updates);
      toast.success(`${user.email} updated`);
      if (updates.organization_id !== undefined || updates.role !== undefined) {
        toast.info("Their existing sessions were ended — they'll need to sign back in.");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="edit-user-modal">
        <DialogHeader>
          <DialogTitle>Edit user — {user.email}</DialogTitle>
          <DialogDescription>
            Change name, role, or move this user to a different organization.
            Changing role or organization will end the user's existing sessions.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3" data-testid="edit-user-form">
          <div>
            <Label htmlFor="edit-name">Full name</Label>
            <Input id="edit-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-10 rounded-lg mt-1.5" data-testid="edit-user-name" />
          </div>
          <div>
            <Label htmlFor="edit-company">Job title / dept</Label>
            <Input id="edit-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="h-10 rounded-lg mt-1.5" data-testid="edit-user-company" />
          </div>
          <div>
            <Label htmlFor="edit-role">Role</Label>
            <select
              id="edit-role" value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm mt-1.5"
              data-testid="edit-user-role"
            >
              {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label} — {r.description}</option>)}
            </select>
          </div>

          {form.role !== "admin" ? (
            <div className="rounded-lg border-2 border-teal-500 bg-teal-50/60 p-3.5 space-y-2.5" data-testid="edit-user-org-section">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-teal-700" />
                <div className="text-sm font-semibold text-teal-900 uppercase tracking-wider">Organization</div>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-700 bg-white rounded-md px-2.5 py-1.5 border border-slate-200" data-testid="edit-user-current-org">
                <span className="text-slate-500">Currently in:</span>
                <span className="font-semibold text-slate-900">{currentOrgName}</span>
              </div>
              <select
                value={form.organization_id}
                onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm bg-white"
                data-testid="edit-user-org-select"
                required
              >
                <option value="">Move to organization...</option>
                {organizations.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name} ({o.user_count} {o.user_count === 1 ? "user" : "users"})
                  </option>
                ))}
              </select>
              {orgChanged ? (
                <div className="rounded-md bg-teal-600 text-white px-3 py-2 text-xs flex items-center gap-2" data-testid="edit-user-org-change-banner">
                  <ArrowRight className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>
                    Moving <span className="font-semibold">{user.name}</span> from{" "}
                    <span className="font-semibold underline">{currentOrgName}</span> to{" "}
                    <span className="font-semibold underline">{newOrgName}</span>
                  </span>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
              <ShieldAlert className="h-3.5 w-3.5 inline mr-1.5" />
              {roleBecomingAdmin
                ? `Promoting ${user.name} to Platform Admin will remove them from "${currentOrgName}" and give them management access to ALL organizations.`
                : "Platform Admins don't belong to an organization."}
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="ai-cta rounded-lg" data-testid="edit-user-submit">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
              Save changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
